import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cn } from "@/lib/cn";

export const Card = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={cn("card", className)}>{children}</div>
);

type BadgeTone = "neutral" | "brand" | "green" | "amber" | "red" | "slate";
const toneMap: Record<BadgeTone, string> = {
  neutral: "bg-slate-100 text-slate-700",
  brand: "bg-brand-50 text-brand-700",
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-red-50 text-red-700",
  slate: "bg-slate-800 text-white",
};

export const Badge = ({
  tone = "neutral",
  dot,
  children,
  className,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <span className={cn("tag", toneMap[tone], className)}>
    {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
    {children}
  </span>
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  block?: boolean;
  loading?: boolean;
};
const variantMap: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
  danger: "btn bg-red-600 text-white hover:bg-red-700",
};
const sizeMap: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "",
  lg: "px-6 py-3 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", block, loading, className, children, disabled, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(variantMap[variant], sizeMap[size], block && "w-full", className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  );
});

export const EmptyState = ({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-16 text-center">
    {icon && (
      <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
        {icon}
      </div>
    )}
    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    {body && <p className="mt-2 max-w-md text-sm text-slate-600">{body}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const Stepper = ({
  steps,
  current,
}: {
  steps: { id: string | number; label: string }[];
  current: number;
}) => (
  <ol className="flex items-center gap-3">
    {steps.map((step, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <li key={step.id} className="flex flex-1 items-center gap-3">
          <div
            className={cn(
              "flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-semibold transition",
              done && "bg-emerald-600 text-white",
              active && "bg-brand-600 text-white shadow-pop",
              !done && !active && "bg-slate-200 text-slate-500"
            )}
          >
            {done ? (
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                <path
                  d="M4 10.5l3 3 9-9"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              i + 1
            )}
          </div>
          <span
            className={cn(
              "hidden text-xs font-medium sm:block",
              active ? "text-slate-900" : "text-slate-500"
            )}
          >
            {step.label}
          </span>
          {i < steps.length - 1 && (
            <div className={cn("h-px flex-1 bg-slate-200", done && "bg-emerald-500/70")} />
          )}
        </li>
      );
    })}
  </ol>
);

export const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn("animate-pulse rounded-xl bg-slate-200/70", className)} />
);

export const SectionHeading = ({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
    </div>
    {action && <div>{action}</div>}
  </div>
);
